<template>
    <base-sheet
        id="auto-response-sidebar"
        width="lg"
        static-backdrop
        :title="editMode ? $t('edit-auto-response') : $t('add-auto-response')"
        :ok-text="editMode ? $t('save') : $t('add')"
        :ok-disabled="disabledSaveBtn"
        :busy="loading"
        @ok="editMode ? editAutoResponse() : createAutoResponse()"
        @hidden="clearForm()"
    >
        <div id="auto-response" class="d-flex flex-column add-edit-form">
            <form>
                <!-- Title -->
                <div class="form-group mb-5">
                    <label class="form-title font-nunito" for="name">{{$t('auto-response-title')}}</label>
                    <input
                        type="text"
                        name="name"
                        class="form-control form-border font-nunito"
                        :placeholder="$t('template-name-placeholder')"
                        v-model.trim="autoResponse.name">
                </div>

                <!-- Keywords -->
                <div class="form-group mb-4">
                    <div class="d-flex flex-column">
                        <div class="d-flex justify-content-between">
                            <div class="d-flex">
                                <label class="form-title mb-0 font-nunito" for="audience">{{$t('keywords')}}</label>
                                <div class="ml-2 d-flex" v-popover.right="{ name:'auto-response-keywords' }" type="button" ref="rules">
                                    <font-awesome-icon class="align-self-center text-black info-circle" :icon="['fa', 'info-circle']" />
                                </div>
                            </div>
                            <div class="d-flex">
                                <input type="checkbox" class="form-check-input mr-2 align-self-center" id="isWithoutKeywords" v-model="isWithoutKeywords">
                                <label for="isWithoutKeywords" class="m-0 pointer text-black fs-14 font-nunito">
                                    {{ $t('no-keywords') }}
                                </label>
                            </div>
                        </div>
                        <small class="text-left text-black font-nunito text-dark-brown mt-1" v-if="isWithoutKeywords">{{ $t('autoresp-without-keywords') }}</small>
                        <small class="text-left text-black font-nunito text-dark-brown mt-1" v-else>{{ $t('keywords-auto-response') }}</small>
                    </div>
                    <div v-if="!isWithoutKeywords">
                        <base-chips-input
                            class="mt-3"
                            v-model="autoResponse.keywords"
                            separator=","
                            commit-on-blur
                            :parse="parseKeyword"
                            :placeholder="$t('autoResponse-key-placeholder')"
                            :add-placeholder="$t('add-keyword')"
                            :label="$t('keywords')"
                        ></base-chips-input>
                    </div>
                </div>

                <!-- Content -->
                <div class="form-group" :class="{ 'mb-5': !errors.has('content') }">
                    <label class="form-title font-nunito d-flex"  for="name">
                        {{ $t('autoresponse-sent')}}
                        <div class="ml-2 d-flex" v-popover.right="{ name:'create-auto-response' }" type="button" ref="rules">
                            <font-awesome-icon class="align-self-center text-black info-circle" :icon="['fa', 'info-circle']" />
                        </div>
                    </label>
                    <!-- Gabarits -->
                    <select-dropdown
                        :options="options"
                        :selected="selectedTemplate"
                        v-on:selectTemplate="chooseTemplate($event)"
                        :showLabel="false"
                        :type="'template'">
                    </select-dropdown>
                    <textarea
                        type="text"
                        name="message"
                        class="form-control form-border font-nunitor mt-2"
                        :placeholder="$t('template-text-placeholder')"
                        v-model.trim="autoResponse.content"
                        v-validate="'max:1600'"
                        data-vv-as="Message"
                        data-vv-name="content"
                        :class="{ 'is-invalid': submitted && errors.has('content') }">
                    </textarea>
                    <sms-counter :sms="autoResponse.content" v-on:limitExceeded="smsLimitExceeded = $event"></sms-counter>
                </div>

                <div class="form-group mb-4">
                    <div class="d-flex flex-column">
                        <label class="font-nunito-SemiBold fs-14 text-dark" for="audience">{{ $t('faculative-tags')}}</label>
                        <small class="text-left text-black font-nunito">{{ $t('choose-a-tag') }}</small>
                    </div>
                    <base-chips-input
                        class="mt-2"
                        v-model="autoResponse.tags"
                        :suggestions="tagNames"
                        :allow-create="false"
                        :placeholder="$t('add-tag')"
                        :label="$t('faculative-tags')"
                    ></base-chips-input>
                </div>

                <div class="invalid-feedback text-left mb-5 font-nunito"
                    v-if="submitted && errors.has('content')">
                    {{ errors.first('content') }}
                </div>
            </form>
        </div>
    </base-sheet>
</template>

<script>

import Vue from 'vue';

// Import internal libraries
import Alert  from '@/helpers/Alert.js';

import ReplaceMessageMixin from '@/core/mixins/ReplaceMessageMixin';

// Import Components
import SelectDropdown from '@/core/components/SelectDropdown.vue';
import BaseSheet      from '@/core/components/BaseSheet.vue';
import BaseChipsInput from '@/core/components/BaseChipsInput.vue';
import SmsCounter     from '@/connectors/sms/components/SmsCounter.vue';

// Import external libraries
import {
    mapActions,
    mapState,
    mapGetters
} from 'vuex';

const clone = require('rfdc')();

export default {
    mixins: [ReplaceMessageMixin],

    props: {
        editMode: {
            type:    Boolean,
            default: false
        }
    },
    components: {
        BaseSheet,
        BaseChipsInput,
        SelectDropdown,
        SmsCounter
    },

    mounted () {
        // Load the tenant tags for the tag picker
        this.getTags();

        // Get the temaplate
        this.getTemplate().then(() => {
            this.options.push({
                'id':      null,
                'name':    this.$i18n.t('choose-template'),
                'content': ''
            });
            for (const templateId in this.templates) {
                this.options.push({
                    'id':      templateId,
                    'name':    this.templates[templateId].attributes.name,
                    'content': this.templates[templateId].attributes.content
                });
            }
        });
    },

    data () {
        return {
            selectedTemplate:  this.$i18n.t('choose-template'),
            smsLimitExceeded:  false,
            disabled:          false,
            loading:           false,
            submitted:         false,
            isWithoutKeywords: false,
            autoResponse:      {
                'name':       '',
                'content':    '',
                'is-enabled': true,
                'keywords':   [],
                'tags':       []
            },
            options: [],
        };
    },

    watch: {
        /**
         * Selected Auto Response watcher
         *
         * @param {Object}  value   The value
         */
        selectedAutoResponse (value) {
            if (!value) {
                return;
            }

            const data = ['name', 'content', 'is-enabled', 'keywords', 'tags'];
            data.forEach(element => {
                this.autoResponse[element] = clone(value.attributes[element]);
            });

            if (!Array.isArray(this.autoResponse.tags)) {
                this.autoResponse.tags = [];
            }

            this.isWithoutKeywords = this.autoResponse.keywords.length === 0;
        },

        isWithoutKeywords (isWithout) {
            if (isWithout && this.autoResponse.keywords.length > 0) {
                this.autoResponse.keywords = [];
            }
        }
    },

    computed: {
        /**
         * Load templates
         */
        ...mapState('core/templates', { 'templates': 'templates' }),

        /**
         * Auto responses States
         */
        ...mapState('core/autoResponses', ['selectedAutoResponse']),

        /**
         * Auto responses Getters
         */
        ...mapGetters('core/autoResponses', ['usedKeywords', 'systemKeywords']),

        /**
         * All the tenant tags, used to feed the tag picker.
         */
        ...mapGetters('core/tags', { 'allTags': 'tags' }),

        tagNames () {
            return Object.values(this.allTags || {}).map(tag => tag.attributes.name);
        },

        /**
         * Check if the submit button is disabled or not
         *
         * @return boolean
         */
        disabledSaveBtn () {
            if (this.loading ||
                this.autoResponse.name === '' ||
                this.autoResponse.content === '' ||
                this.smsLimitExceeded ||
                (!this.isWithoutKeywords && this.autoResponse.keywords.length === 0)
            ) {
                return true;
            }
            return false;
        }
    },

    methods: {
        /**
         * Load templates
         */
        ...mapActions('core/templates', { getTemplate: 'index' }),

        /**
         * Auto response actions
         */
        ...mapActions('core/autoResponses', ['create', 'update', 'delete', 'unselectAutoResponse']),

        /**
         * Tags actions
         */
        ...mapActions('core/tags', { getTags: 'index' }),

        /**
         * Update selected option
         *
         * @return    void
         */
        chooseTemplate (option) {
            if (option.id === null) {
                this.selectedTemplate    = option.name;
                this.disabled            = option.id === null;
            } else if (this.autoResponse.content === '') {
                this.selectedTemplate     = option.name;
                this.autoResponse.content = option.content;
                this.disabled             = option.id === null;
            } else {
                this.confirmReplaceMessage()
                    .then((confirmed) => {
                        if (confirmed) {
                            this.selectedTemplate     = option.name;
                            this.autoResponse.content = option.content;
                        }
                    });
            }
        },

        /**
         * Check a typed keyword before adding it to the auto response
         *
         * @param {string}  text  the typed keyword
         *
         * @return {object}
         */
        parseKeyword (text) {
            const keyword = text.replace(/\s+/g, '').toLowerCase();
            const quoted  = `"${keyword.toUpperCase()}"`;

            if (this.usedKeywords.some(key => key.toLowerCase() === keyword)) {
                return { error: this.$i18n.t('keyword-used', { keyword: quoted }) };
            }

            if (this.systemKeywords.some(key => key.toLowerCase() === keyword)) {
                return { error: this.$i18n.t('system-keyword', { keyword: quoted }) };
            }

            return { value: keyword };
        },

        /**
         *  Create a new auto response
         */
        createAutoResponse () {
            if (this.disabledSaveBtn) {
                return;
            }

            Vue.prototype.$validation = this.$validator;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    return;
                }

                // Spinner on and disable button
                this.loading   = true;
                this.submitted = true;

                this.create({ attributes: this.autoResponse })
                    .then(() => {
                        this.loading   = false;
                        this.submitted = false;

                        Alert.success(this.$i18n.t('auto-response-created'));
                        this.$bvModal.hide('auto-response-sidebar');
                    })
                    .catch(() => {
                        this.loading   = false;
                        this.submitted = false;
                    });
            });
        },

        /**
         *  Edit an auto response
         */
        editAutoResponse () {
            if (this.disabledSaveBtn) {
                return;
            }

            Vue.prototype.$validation = this.$validator;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    return;
                }

                this.loading   = true;
                this.submitted = true;

                this.update({
                    id:         this.selectedAutoResponse.id,
                    attributes: this.autoResponse
                })
                    .then(() => {
                        this.loading = false;
                        Alert.success(this.$i18n.t('auto-response-updated'));
                        this.$bvModal.hide('auto-response-sidebar');
                    })
                    .catch(() => {
                        this.loading = false;
                    });
            });
        },

        /**
         * Clear the auto response form
         */
        clearForm () {
            this.loading           = false;
            this.isWithoutKeywords = false;
            this.autoResponse      = {
                'name':       '',
                'content':    '',
                'is-enabled': true,
                'keywords':   [],
                'tags':       []
            },
            this.selectedTemplate = this.$i18n.t('choose-template');

            if (this.editMode) {
                this.unselectAutoResponse({ id: this.selectedAutoResponse.id });
            }
        }
    }
};
</script>
